import React, { useState, useEffect } from "react";
import { Select, DatePicker, Row, Col, Spin } from "antd";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  CategoryScale,
  Legend,
} from "chart.js";
import { getDeviceNameFromId, DeviceId } from "../common/helper/helper";
import moment from "moment";
import { API_URL } from "../../config";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  CategoryScale,
  Legend,
);

const { Option } = Select;
const { RangePicker } = DatePicker;

const VitalDataTab = React.memo(function VitalDataTab(props) {
  const { t } = useTranslation();
  const { patient_id } = props;

  const initialChartState = {
    labels: [],
    datasets: [
      {
        label: "Device Data",
        data: [],
        fill: false,
        borderColor: "rgba(75,192,192,1)",
        tension: 0.1,
      },
    ],
  };
  const initialChartOptions = (units, deviceName) => ({
    responsive: true,
    plugins: {
      legend: {
        position: "bottom",
        align: "center",
      },
      title: {
        display: true,
        text: `Device Data Chart: ${deviceName}`,
      },
    },
    scales: {
      x: {
        display: false,
        title: {
          display: false,
          text: "Date",
        },
      },
      y: {
        display: true,
        title: {
          display: true,
          text: `Value (${units})`,
        },
        beginAtZero: true,
      },
    },
  });

  const [dateRange1, setDateRange1] = useState([
    dayjs().startOf("day"),
    dayjs().endOf("day"),
  ]);
  const [deviceId1, setDeviceId1] = useState(DeviceId.Weight);
  const [chartData1, setChartData1] = useState(initialChartState);
  const [loading1, setLoading1] = useState(false);
  const [units, setUnits] = useState("");
  const [selectedDeviceName, setSelectedDeviceName] = useState(
    getDeviceNameFromId(DeviceId.Weight),
  );

  useEffect(() => {
    fetchData(deviceId1, dateRange1, setChartData1, setLoading1, setUnits);
  }, []);

  useEffect(() => {
    if (dateRange1.length === 2) {
      fetchData(deviceId1, dateRange1, setChartData1, setLoading1, setUnits);
    }
  }, [dateRange1, deviceId1]);

  const handleDatePickerChange1 = (dates, dateStrings) => {
    if (dates) {
      setDateRange1([dates[0].startOf("minute"), dates[1].endOf("minute")]);
    } else {
      setDateRange1([]);
    }
  };

  const fetchData = async (
    deviceId,
    dateRange,
    setChartData,
    setLoading,
    setUnits,
  ) => {
    setLoading(true);
    const [startDate, endDate] = dateRange.map((date) =>
      date.format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
    );

    try {
      if (deviceId === DeviceId.BPCombined) {
        const [sysResponse, diaResponse] = await Promise.all([
          fetch(
            `${API_URL}/device/readings/?patient_id=${patient_id}&device_id=${DeviceId.BPSys}&from_datetime=${startDate}&to_datetime=${endDate}&skip=0&limit=100`,
          ),
          fetch(
            `${API_URL}/device/readings/?patient_id=${patient_id}&device_id=${DeviceId.BPDia}&from_datetime=${startDate}&to_datetime=${endDate}&skip=0&limit=100`,
          ),
        ]);

        const sysData = await sysResponse.json();
        const diaData = await diaResponse.json();

        const combinedData = [
          ...sysData.map((item) => ({ ...item, type: "Systolic" })),
          ...diaData.map((item) => ({ ...item, type: "Diastolic" })),
        ];

        combinedData.sort(
          (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
        );

        const sysSorted = combinedData.filter(
          (item) => item.type === "Systolic",
        );
        const diaSorted = combinedData.filter(
          (item) => item.type === "Diastolic",
        );

        const labels = combinedData.map((item) =>
          moment(item.timestamp).format("YYYY-MM-DD HH:mm:ss"),
        );

        setChartData({
          labels: [...new Set(labels)],
          datasets: [
            {
              label: "Systolic",
              data: sysSorted.map((item) => item.value),
              fill: false,
              borderColor: "rgba(75,192,192,1)",
              tension: 0.1,
            },
            {
              label: "Diastolic",
              data: diaSorted.map((item) => item.value),
              fill: false,
              borderColor: "rgba(255,99,132,1)",
              tension: 0.1,
            },
          ],
        });
        setUnits(sysData[0]?.units || "units");
      } else {
        const response = await fetch(
          `${API_URL}/device/readings/?patient_id=${patient_id}&device_id=${deviceId}&from_datetime=${startDate}&to_datetime=${endDate}&skip=0&limit=100`,
        );
        const data = await response.json();

        data.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

        const labels = data.map((item) =>
          moment(item.timestamp).format("YYYY-MM-DD HH:mm:ss"),
        );
        const values = data.map((item) => item.value);

        setChartData({
          labels,
          datasets: [
            {
              label: `Data for Device ${getDeviceNameFromId(deviceId)}`,
              data: values,
              fill: false,
              borderColor: "rgba(75,192,192,1)",
              tension: 0.1,
            },
          ],
        });
        setUnits(data[0]?.units || "units");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeviceChange = (value) => {
    setDeviceId1(value);
    setSelectedDeviceName(getDeviceNameFromId(value));
  };

  const rangePresets = [
    {
      label: t('callender.currentDay'),
      value: [dayjs().startOf("day"), dayjs().endOf("day")],
    },
    {
      label: t('callender.currentWeek'),
      value: [dayjs().startOf("week"), dayjs().endOf("week")],
    },
    {
      label: t('callender.currentMonth'),
      value: [dayjs().startOf("month"), dayjs().endOf("month")],
    },
    {
      label: t('callender.previousDay'),
      value: [
        dayjs().subtract(1, "day").startOf("day"),
        dayjs().subtract(1, "day").endOf("day"),
      ],
    },
    {
      label: t('callender.previousWeek'),
      value: [
        dayjs().subtract(1, "week").startOf("week"),
        dayjs().subtract(1, "week").endOf("week"),
      ],
    },
    {
      label: t('callender.previousMonth'),
      value: [
        dayjs().subtract(1, "month").startOf("month"),
        dayjs().subtract(1, "month").endOf("month"),
      ],
    },
  ];

  return (
    <div className="vital-data-container">
      <Row gutter={16}>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Select
            defaultValue={DeviceId.Weight}
            style={{ width: "100%" }}
            onChange={handleDeviceChange}
          >
            {Object.values(DeviceId)
              .filter((id) => id !== DeviceId.BPSys && id !== DeviceId.BPDia)
              .map((id) => (
                <Option key={id} value={id}>
                  {getDeviceNameFromId(id)}
                </Option>
              ))}
          </Select>
        </Col>
        <Col xs={24} sm={24} md={8} lg={12}>
          <RangePicker
            presets={rangePresets}
            showTime
            format="YYYY/MM/DD HH:mm:ss"
            onChange={handleDatePickerChange1}
            value={dateRange1}
          />
        </Col>
      </Row>
      <div className="chart-container">
        {loading1 ? (
          <Spin size="large" />
        ) : (
          <Line
            data={chartData1}
            options={initialChartOptions(units, selectedDeviceName)}
          />
        )}
      </div>
    </div>
  );
});

export default VitalDataTab;
